/**
 * Session state and the idle lock.
 *
 * "Auto-locks after 5 minutes idle; tapping the name chip locks it by hand at
 * shift change."
 *
 * The session token lives in memory only — never localStorage, never a cookie.
 * Reloading the page locks the app, which is the behaviour you want on a tablet
 * that gets passed around. What survives a reload is the wrapped credential in
 * IndexedDB, and that needs the PIN.
 */

import { api, setSessionToken } from './api';
import { personSummary, unlock as unwrapCredential, unlockByPin } from './credential';
import { isNetworkError } from './connectivity';

export const IDLE_LOCK_MS = 5 * 60 * 1000;

/**
 * The unwrapped device credential, held only while the tablet is unlocked.
 *
 * It is what the outbox needs to get a session when the signal comes back
 * after a spell offline (the session was granted locally, with no token) or
 * after the token's fifteen minutes ran out mid-shift. Memory only, like the
 * token; the lock clears it, and the PIN is needed to get it back.
 */
let heldCredential: string | null = null;

export interface Session {
  user: { id: string; name: string; role: 'owner' | 'clerk' };
  permissions: string[];
  device: { id: string; label: string };
  expiresAt: string;
  /** True when the PIN was accepted locally but the server could not be reached. */
  offline: boolean;
}

type Listener = (session: Session | null) => void;

let current: Session | null = null;
const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) listener(current);
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  listener(current);
  return () => listeners.delete(listener);
}

export function getSession(): Session | null {
  return current;
}

/**
 * Who + PIN → that person's credential (locally, from the cached wrap) →
 * session token (if there is a connection). Any registered tablet, anyone
 * with a login.
 *
 * A wrong PIN throws from `unwrapCredential` before anything touches the
 * network, so a bad PIN costs nothing and leaks nothing.
 */
export async function unlockWithPin(userId: string, pin: string): Promise<Session> {
  const credential = await unwrapCredential(userId, pin);
  heldCredential = credential;

  try {
    const result = await api.unlock(credential);
    setSessionToken(result.token);
    current = {
      user: result.user,
      permissions: result.permissions,
      device: result.device,
      expiresAt: result.expiresAt,
      offline: false,
    };
  } catch (err) {
    // A network failure is not a refusal. The PIN was right, so the clerk gets
    // in and works; writes queue until there is a signal (step 9). A refusal
    // from the server — revoked device, disabled login — is a real answer and
    // must not be swallowed.
    if (!isNetworkError(err)) {
      heldCredential = null;
      throw err;
    }

    const summary = await personSummary(userId);
    if (!summary) throw err;
    setSessionToken(null);
    current = {
      user: { id: summary.userId, name: summary.name, role: summary.role },
      permissions: [],
      device: { id: '', label: 'this tablet' },
      expiresAt: new Date(Date.now() + IDLE_LOCK_MS).toISOString(),
      offline: true,
    };
  }

  startIdleTimer();
  emit();
  return current;
}

/**
 * The PIN alone — the pad's way in. The tablet works out whose it is.
 */
export async function unlockWithPinOnly(pin: string): Promise<Session> {
  const who = await unlockByPin(pin);
  heldCredential = who.credential;

  try {
    const result = await api.unlock(who.credential);
    setSessionToken(result.token);
    current = {
      user: result.user,
      permissions: result.permissions,
      device: result.device,
      expiresAt: result.expiresAt,
      offline: false,
    };
  } catch (err) {
    if (!isNetworkError(err)) {
      heldCredential = null;
      throw err;
    }
    setSessionToken(null);
    current = {
      user: { id: who.userId, name: who.name, role: who.role },
      permissions: [],
      device: { id: '', label: 'this tablet' },
      expiresAt: new Date(Date.now() + IDLE_LOCK_MS).toISOString(),
      offline: true,
    };
  }

  startIdleTimer();
  emit();
  return current;
}

/**
 * A fresh session token from the held credential, without the PIN.
 *
 * True when the tablet now has a live session. Turns an offline session into
 * a real one (real user id, real permissions) or renews an expired token. A
 * refusal — device revoked, login disabled — locks the tablet the way it
 * would have been locked had the refusal come at unlock.
 */
export async function reconnect(): Promise<boolean> {
  if (!current) return false;
  if (!heldCredential) return !current.offline;

  try {
    const result = await api.unlock(heldCredential);
    setSessionToken(result.token);
    current = {
      user: result.user,
      permissions: result.permissions,
      device: result.device,
      expiresAt: result.expiresAt,
      offline: false,
    };
    emit();
    return true;
  } catch (err) {
    if (isNetworkError(err)) return false;
    await lock();
    return false;
  }
}

/** The name chip at shift change, the idle timer, and a disabled login. */
export async function lock(): Promise<void> {
  const had = current;
  current = null;
  heldCredential = null;
  stopIdleTimer();
  emit();

  // Best effort: the local lock has already happened, and a failure here only
  // means the session row expires on its own a few minutes later.
  if (had && !had.offline) {
    try {
      await api.lock();
    } catch {
      /* already locked locally */
    }
  }
  setSessionToken(null);
}

// ---------------------------------------------------------------- idle

let idleTimer: ReturnType<typeof setTimeout> | null = null;

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const;

function onActivity(): void {
  if (current) resetIdleTimer();
}

function resetIdleTimer(): void {
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    void lock();
  }, IDLE_LOCK_MS);
}

function startIdleTimer(): void {
  for (const event of ACTIVITY_EVENTS) {
    document.addEventListener(event, onActivity, { passive: true, capture: true });
  }
  resetIdleTimer();
}

function stopIdleTimer(): void {
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = null;
  for (const event of ACTIVITY_EVENTS) {
    document.removeEventListener(event, onActivity, { capture: true });
  }
}

/**
 * Tabs by role, exactly as the prototype builds them.
 *
 * Owner:  Approvals, Report, Summary, Pay run  (+ the Settings button)
 * Clerk:  Labour, Diesel, Misc, Report, Summary — whichever the owner allowed
 * Both:   Permits — the owner always; a clerk with the `plants` permission
 */
export interface TabDef {
  id: string;
  label: string;
  owner?: boolean;
  clerk?: boolean;
  /** Owner always; a clerk with `perm`. */
  both?: boolean;
  perm?: string;
}

export const TABS: readonly TabDef[] = [
  { id: 'approvals', label: 'Approvals', owner: true },
  { id: 'labour', label: 'Labour', perm: 'labour', clerk: true },
  { id: 'diesel', label: 'Diesel', perm: 'diesel', clerk: true },
  { id: 'misc', label: 'Misc', perm: 'misc', clerk: true },
  { id: 'report', label: 'Report', perm: 'report' },
  { id: 'summary', label: 'Summary', perm: 'summary' },
  { id: 'pay', label: 'Pay run', owner: true },
  { id: 'permits', label: 'Permits', both: true, perm: 'plants' },
] as const;

export function isOwner(session: Session | null): boolean {
  return session?.user.role === 'owner';
}

/** UI convenience only — the Worker checks this again on every request. */
export function can(session: Session | null, perm: string | undefined): boolean {
  if (!session) return false;
  if (session.user.role === 'owner') return true;
  return perm !== undefined && session.permissions.includes(perm);
}

export function allowedTabs(session: Session | null): TabDef[] {
  return TABS.filter((tab) => {
    if (tab.both) return isOwner(session) || can(session, tab.perm);
    if (tab.owner) return isOwner(session);
    if (tab.clerk && isOwner(session)) return false;
    return can(session, tab.perm);
  });
}
